<template>
  <div class="log">
    <div class="log-header">
      <div class="title">
        <icon class="icon" name="fa-code" />
        <span>Log</span>
      </div>
      <div>
        <icon class="icon clear" name="fa-trash" @click="clear" />
        <icon
          class="icon reload"
          name="fa-recycle"
          @click="loadEntries"
        />
      </div>
    </div>
    <div class="log-content">
      <div v-if="!isSupported">
        <danger title="Not Supported">
          IndexedDB is required for diagnostic logging. You browser does not
          support IndexedDB. Please upgrade to Microsoft Edge version 79 or
          later.
        </danger>
      </div>
      <div v-if="isSupported && loaded">
        <div class="filter-controls">
          <drop-down
            id="level-filter"
            label="Show:"
            :items="levelItems"
            v-model="levelFilter"
          />
          <text-field
            id="start-filter"
            label="From:"
            placeholder="Start date"
            v-model="startFilter"
          />
          <text-field
            id="end-filter"
            label="To:"
            placeholder="End date"
            v-model="endFilter"
          />
          <button
            type="button"
            class="btn btn-primary ml-1"
            @click="loadEntries"
          >
            Apply
          </button>
        </div>
        <div>
          <span class="refresh-time">Updated: {{ $filters.log(lastRefresh) }}</span>
        </div>
        <table v-if="logEntries.length > 0">
          <thead>
            <th></th>
            <th>Timestamp</th>
            <th>Message</th>
            <th>Route</th>
            <th>Component</th>
            <th>Additional Data</th>
          </thead>
          <tbody v-for="entry in logEntries" :key="entry.id">
            <tr :class="entry.level">
              <td><icon class="icon" :name="getIcon(entry)" /></td>
              <td>{{ $filters.log(entry.timestamp) }}</td>
              <td>{{ entry.message }}</td>
              <td>{{ entry.route }}</td>
              <td>{{ entry.component }}</td>
              <td>
                <a
                  v-if="entry.data"
                  href="#"
                  @click.prevent="toggleData(entry)"
                  >{{ expanded === entry.id ? "Hide" : "Show" }}</a
                >
              </td>
            </tr>
            <tr v-if="expanded === entry.id" :class="entry.level">
              <td colspan="6">
                <pre>{{ getData(entry) }}</pre>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else>No diagnostic information available.</p>
      </div>
    </div>
  </div>
</template>
<script>
import moment from "moment";
import Danger from "@/components/common/banners/Danger.vue";
import diagnostics, {
  QueryBuilder,
  DEBUG,
  INFO,
  WARNING,
  ERROR,
} from "@/services/diagnostics/diagnosticsService";

const clear = async function () {
  await diagnostics.clear();
  await this.loadEntries();
};

const loadEntries = async function () {
  try {
    const query = new QueryBuilder()
      .forLevel(this.levelFilter)
      .after(this.startFilter)
      .before(this.endFilter);

    const entries = await diagnostics.get(query);

    entries.reverse();
    this.logEntries = entries;

    this.lastRefresh = moment().local().toDate();
    this.loaded = true;
  } catch (error) {
    console.error(`Unable to load log entries: ${error}`);
  }
};

export default {
  components: { Danger },
  data: function () {
    return {
      logEntries: [],
      loaded: false,
      expanded: -1,
      lastRefresh: null,
      levelFilter: "",
      startFilter: "",
      endFilter: "",
    };
  },
  computed: {
    isSupported: function () {
      return diagnostics.isSupported();
    },
    levelItems: function () {
      const items = [];

      items.push({ label: "All Entries", value: "" });
      items.push({ label: "Debug Messages", value: DEBUG });
      items.push({ label: "Information", value: INFO });
      items.push({ label: "Warnings", value: WARNING });
      items.push({ label: "Errors", value: ERROR });

      return items;
    },
  },
  methods: {
    loadEntries,
    clear,
    getIcon: function (entry) {
      switch (entry.level) {
        case DEBUG:
          return `fa-code ${entry.level}`;
        case INFO:
          return `fa-exclamation-circle ${entry.level}`;
        case WARNING:
        case ERROR:
          return `fa-exclamation-triangle ${entry.level}`;
      }
    },
    getData: function (entry) {
      return JSON.stringify(entry.data, null, 2);
    },
    toggleData: function (entry) {
      if (this.expanded !== entry.id) {
        this.expanded = entry.id;
      } else {
        this.expanded = -1;
      }
    },
  },
  mounted: async function () {
    await this.loadEntries();
  },
};
</script>
<style lang="scss" scoped>
@use "sass:color";
@import "@/_css/variables.scss";

.log {
  height: 100%;

  & > .log-header {
    height: 100px;
    background-color: $gray-800;
    color: $text-inverted;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0 10px;
    font-size: 2rem;

    & > .title {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      & > .icon {
        margin-right: 5px;
      }
    }

    & .reload,
    & .clear {
      cursor: pointer;
    }
  }

  & > .log-content {
    padding: 10px;

    & .filter-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: flex-end;
    }

    & table {
      border-collapse: collapse;

      & tr {
        &.info {
          background-color: color.adjust($storm, $lightness: 50%);
        }
        &.warning {
          background-color: color.adjust($gold, $lightness: 25%);
        }
        &.error {
          background-color: color.adjust($red, $lightness: 50%);
        }
      }

      & th,
      & td {
        padding: 0;
        margin: 0;
      }

      & td {
        & > .icon {
          color: black;

          &.info {
            color: $storm;
          }

          &.warning {
            color: $gold;
          }

          &.error {
            color: $red;
          }
        }
      }

      & th {
        font-size: 1.1rem;
        font-weight: bold;
        border-bottom: 1px solid black;
        margin-bottom: 5px !important;
      }
    }
  }
}
</style>
